import { autoinject } from "aurelia-framework";
import { bindable } from 'aurelia-typed-observable-plugin';
import { IForm, State, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { Toaster } from 'app/shared/lib/toaster';
import { deepClone } from 'app/shared/lib/utilities';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { AuthService } from 'app/um/services/auth-service';
import { validate } from 'app/shared/elements/ess-rule';
import { apiCall } from 'app/shared/lib/api/api';
import { getReference } from 'app/shared/models/essette-types';
import { Router } from 'aurelia-router';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { noop } from 'app/shared/lib/noop';
import { AccountContext } from 'app/core/lib/account-context';
import { openAuthSummary } from 'app/um/components/summary/auth-summary';
import { MemberDisplay } from 'app/cd/elements/members/member-display';
import { DateTimeValue } from 'app/shared/lib/date-time';
import { IDocumentsConfig } from 'app/core/models/essette-interfaces';
import { DocumentsService } from 'app/core/services/documents-service';
import { IApiActionResults } from 'app/shared/lib/api/api-types';
import { guid } from 'app/shared/lib/guid';
import * as CD from 'app/cd/models/essette-api';
import * as UM from 'app/um/models/essette-api';
import * as FM from 'app/fm/models/essette-api';

@autoinject()
export class CopyAuth implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull
    public sourceAuth: UM.Auth | null = null;

    @bindable.objectNull
    public memberPlan: CD.MemberPlan | null = null;

    @bindable.objectNull
    public faxDocument: FM.FaxDocument | null = null;

    protected auth: UM.Auth = {};
    protected users: UM.AuthOwner[] = [];
    protected authTypes: UM.AuthType[] = [];
    protected authPriorities: UM.AuthPriority[] = [];

    protected validateForm = validate;

    protected showAuthSummaryLink: boolean = false;
    protected hasAuthRoute = false;
    protected openAuthSummary = openAuthSummary;
    protected otherReferenceNumberFieldReadonly = false;

    protected copiedAuthId: string | null = null;
    protected assignedToInvalid: boolean | null = null;

    protected id: guid = guid();

    protected documentsConfig: IDocumentsConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        addTargetProperty: (document: UM.AuthDocument) => { document.auth = { id: this.copiedAuthId } }
    };


    ////****************** Element References ***************************************////
    protected $memberDisplay!: MemberDisplay;
    protected $modal!: EssModalCustomElement;
    private resolve: (success: boolean) => void = noop;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        public state: State,
        private readonly logger: AppLogger,
        private readonly toaster: Toaster,
        private readonly env: EnvironmentService,
        private readonly router: Router,
        private readonly accountContext: AccountContext,
        private readonly authService: AuthService,
        private readonly documentService: DocumentsService) {

        this.logger.source = this;
    }

    protected async bind(): Promise<void> {
        this.checkPermissions();

        this.documentService.configure(this.documentsConfig);
    }


    ////****************** Events ***************************************************////
    protected assignedToChanged(): void {
        this.verifyAssignment();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showAuthSummaryLink = this.accountContext.hasRouteAccess('um.auth.summary');
        this.hasAuthRoute = this.accountContext.hasRouteAccess('um.auth');
        this.otherReferenceNumberFieldReadonly = this.env.config.pageConfigurations?.um?.auth?.manageAuths?.create?.modals?.copyAuth?.otherReferenceNumberFieldReadonly || false;
    }

    private async verifyAssignment(): Promise<void> {
        const assignedToId = this.auth?.assignedTo?.id || '';
        if(!this.sourceAuth || !assignedToId) {
            this.assignedToInvalid = null;
            return;
        }

        try {
            this.state.beginProcessing();

            const response = await this.authService.verifyAssignment(assignedToId, [getReference(this.sourceAuth)!]);

            // make sure "assignedTo" hasn't changed while awaiting response
            if(this.auth?.assignedTo?.id !== assignedToId) {
                this.logger.info('Another Verification in Progress, Discarding Stale Results.');
            }
            else {
                this.assignedToInvalid = null;
                if(response.isSuccessful && response.result?.filter?.items) {
                    // if no items come back, assigned to is invalid
                    this.assignedToInvalid = (response.result.filter.items.length === 0);
                }
            }
        }
        finally {
            this.state.endProcessing();
        }
    }

    protected isValidAuthRequestDate(value: string): boolean {
        if(!value) { return true; }
        return new DateTimeValue(this.env.now()).isAfter(new DateTimeValue(value));
    }

    protected sourceAuthChanged(): void {
        this.loadLookups();
        this.reset();
    }

    public async open(): Promise<boolean> {
        if(this.memberPlan) {
            this.$memberDisplay.setPlan(this.memberPlan);
            this.$modal.open();
        }
        return new Promise<boolean>((resolve) => this.resolve = resolve);
    }

    private async loadLookups(): Promise<void> {
        const options: StandardLoadOptions = {
            all: true
        };
        try {
            this.state.beginLoad();

            const [users, types, priorities] = await Promise.all([
                this.authService.loadAuthOwners(options),
                this.authService.loadAuthTypes(true, options),  //load all auth types, even IP only ones
                this.authService.loadAuthPriorities(options)
            ]);

            this.users = users.results;
            this.authTypes = types.results;
            this.authPriorities = priorities.results;
        } catch(error) {
            this.logger.error('Error loading lookups', error);
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    private getAuthCopyOption(value: string | undefined, yesText?: string, noText?: string): string {
        if(!yesText) yesText = 'yes';
        if(!noText) noText = 'no';

        return value ? yesText : noText;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async saveAuth(): Promise<IApiActionResults> {
        let isSuccessful = false;
        try {
            if(!this.sourceAuth || !this.memberPlan) {
                throw new Error('Missing Source Auth or Member Plan');
            }

            this.state.beginSave();

            const saveAuth: UM.Auth = {
                id: this.sourceAuth.id,
                class: getReference(this.auth.class, {
                    subClass: getReference(this.auth.class && this.auth.class.subClass)
                }),
                type: getReference(this.auth.type),
                priority: getReference(this.auth.priority),
                requestedBy: { id: this.env.user.id },
                requestedDateTime: this.auth.requestedDateTime,
                alternateNumber: this.auth.alternateNumber,
                assignedTo: getReference(this.auth.assignedTo),
                serviceDate: this.auth.serviceDate
            };

            if(this.auth.class?.subClass?.isAdmission && !this.auth.type?.flag?.usesPreserviceDay) {
                saveAuth.admission = { date: this.auth.serviceDate?.dateRange?.start };
            } //<=4.6.0 it would use initialDateOfService; but 4.x prefers tum_authservicedatehistory for that

            if(this.memberPlan.id) {
                saveAuth.member = {
                    plan: getReference(this.memberPlan),
                };
            }

            if(this.auth.copy) {
                saveAuth.copy = {
                    admissionSource: this.getAuthCopyOption(this.auth.copy.admissionSource),
                    assessments: this.getAuthCopyOption(this.auth.copy.assessments, 'all', 'none'),
                    attachedDocuments: this.getAuthCopyOption(this.auth.copy.attachedDocuments, 'all', 'none'),
                    chiefComplaint: this.getAuthCopyOption(this.auth.copy.chiefComplaint),
                    diagnoses: this.getAuthCopyOption(this.auth.copy.diagnoses, 'all', 'none'),
                    medications: this.getAuthCopyOption(this.auth.copy.medications, 'all', 'none'),
                    providers: this.getAuthCopyOption(this.auth.copy.providers, 'all', 'none'),
                    services: this.getAuthCopyOption(this.auth.copy.services, 'all', 'none'),
                    source: this.getAuthCopyOption(this.auth.copy.source),
                    customFields: this.getAuthCopyOption(this.auth.copy.customFields, 'all', 'none'),
                };
            }

            const response = await this.authService.copyAuth(saveAuth);
            isSuccessful = response.isSuccessful;
            return response;
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.created('um:auth.$', isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async attachDocument(document: UM.AuthDocument): Promise<IApiActionResults> {
        if(this.documentsConfig && this.documentsConfig.addTargetProperty) {
            this.documentsConfig.addTargetProperty(document);
        }
        try {
            this.state.beginSave();
            return await this.documentService.attachDocument(document);
        }
        catch(error) {
            this.toaster.standard.saved('core:document.$', false);
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    ////****************** IForm ****************************************************////

    public reset(): void {
        if(this.sourceAuth) {

            this.auth = {
                id: this.sourceAuth.id,
                class: this.sourceAuth.class,
                type: this.sourceAuth.type,
                priority: this.sourceAuth.priority,
                serviceDate: deepClone(this.sourceAuth?.serviceDate!)
            }
        }
        else {
            this.auth = {};
        }
        this.auth.requestedDateTime = this.env.now();

        this.auth.copy = {
            admissionSource: 'true',
            assessments: 'true',
            attachedDocuments: 'true',
            chiefComplaint: 'true',
            diagnoses: 'true',
            medications: 'true',
            providers: 'true',
            services: 'true',
            source: 'true',
            customFields: 'true'
        };

        this.copiedAuthId = null;
        this.assignedToInvalid = null;
    }
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }
    public submittedChanges?: any;

    public async submit(): Promise<boolean> {
        const saveAuthResponse = await this.saveAuth();

        this.copiedAuthId = saveAuthResponse?.id;

        // If faxDocument is not null, prepare document to attach to the auth copy
        if(this.faxDocument) {
            const attachDocument: UM.AuthDocument = {
                id: this.faxDocument.document?.id
            };

            await this.attachDocument(attachDocument);
        }

        if(this.copiedAuthId) {
            if(this.faxDocument) {
                this.router.navigate(`#/um/auth/${this.copiedAuthId}/documents/?queueTypeCode=FM.Document&queueId=${this.faxDocument?.queue?.id}&queueTargetId=${this.faxDocument?.document?.id}`);
            }
            else {
                this.router.navigateToRoute('um.auth', { authId: this.copiedAuthId });
            }
            return true;
        }
        else {
            return false;
        }
    }
}
